import {
    track as c9Track,
    ecommerce,
    type product,
    type formfield,
    type item,
    productGroup,
    type coverage
} from 'c9-tracking';
import { type CoverableOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type {
    CoverableDTO,
    CoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { CoverageDTO as OnsaCoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import type { WizardPageComponentName } from '../config/wizardPagesConfig';
import { convertDateToTrackingString, isDate } from './dateUtil';

// Sensitive properties that should not be tracked because of GDPR etc.
// IMPORTANT! All words need to be all lower case.
const SENSITIVE_DATA_LIST = ['cpr', 'cprnumber'];

function isSensitiveData(data: string): boolean {
    return SENSITIVE_DATA_LIST.includes(data.toLowerCase());
}

function convertToFields<T>(data: Array<Record<string, any>>): T[] {
    const fields: T[] = [];

    data.forEach((obj: any) => {
        for (const key in obj) {
            // Don't add data to tracking if it's sensitive
            if (removeSensitiveData(key) === undefined) return;

            // Setup tracking object
            const value = obj[key];
            const field: any = {};

            if (data.length === 1) {
                field.key = key; // use "key" for track.interact
            } else {
                field.id = key; // use "id" for track.form
            }

            if (typeof value === 'object') {
                if (isDate(value)) {
                    // Convert dates
                    field.value = convertDateToTrackingString(value);
                } else {
                    // Convert arrays and objects
                    field.value = JSON.stringify(removeSensitiveData(value));
                }

                fields.push(field as T);
                return;
            }

            // Convert non-object data types
            field.value = String(value);
            fields.push(field as T);
        }
    });

    return fields;
}

function removeSensitiveData<T extends Record<string, any>>(
    data: T[] | T | string
): T[] | T | string | undefined {
    if (Array.isArray(data)) {
        // If data is an array, process each element recursively
        return data.map((obj: any) => removeSensitiveData(obj));
    } else if (typeof data === 'object') {
        // If data is an object, process its properties
        const newObj: Record<string, any> = { ...data };

        for (const [key, value] of Object.entries(newObj)) {
            if (typeof value === 'object' && !isDate(value)) {
                // Recursively redact nested information
                newObj[key] = removeSensitiveData(value);
            } else if (isSensitiveData(key)) {
                // Remove sensitive data from the object
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete newObj[key];
            }
        }

        return newObj as T;
    } else if (!isSensitiveData(data)) {
        // For non-object data types, return as it is
        return data;
    }
}

const track = (description: string, data: Array<Record<string, any>>): void => {
    if (data.length > 1) {
        const formFields = convertToFields<formfield>(data);
        void c9Track.form(description, formFields);
    } else if (data.length === 1) {
        const interactFields = convertToFields<item>(data);
        void c9Track.interact(interactFields, description);
    }
};

const trackAddProduct = (coverable: CoverableDTO | CoverableOnsaDTO): void => {
    const product = convertCoverablesToProducts([coverable])[0];
    void ecommerce.cart.add(product);
};

const trackRemoveProduct = (coverable: CoverableDTO | CoverableOnsaDTO): void => {
    const product = convertCoverablesToProducts([coverable])[0];
    void ecommerce.cart.remove(product);
};

const trackViewProduct = (coverable: CoverableOnsaDTO): void => {
    const product = convertCoverablesToProducts([coverable])[0];
    void ecommerce.viewProduct(product);
};

function trackPurchase(coverables: CoverableDTO[], quoteId: string): void {
    const products = convertCoverablesToProducts(coverables);
    void ecommerce.purchase(products, quoteId);
}

function trackCheckout(coverables: CoverableDTO[], pageStep: number): void {
    const products = convertCoverablesToProducts(coverables);
    void ecommerce.checkout(pageStep, products);
}

const isOnsaCoverageDTO = (
    coverage: CoverageDTO | OnsaCoverageDTO
): coverage is OnsaCoverageDTO => {
    return 'coverageId' in coverage;
};

function convertToProductCoverage(coverage: CoverageDTO | OnsaCoverageDTO): coverage {
    if (isOnsaCoverageDTO(coverage)) {
        const name = coverage.coverageName ?? coverage.coverageId;
        const value =
            coverage.coveragePrice?.yearlyPrice?.amount ??
            coverage.coveragePrice?.monthlyPrice?.amount;

        return value ? { name, value: Math.trunc(value) } : { name };
    }

    return { name: coverage.name ?? '' };
}

function convertCoverablesToProducts(
    coverables: Array<CoverableDTO | CoverableOnsaDTO>
): product[] {
    const products: product[] = [];

    coverables.forEach((coverable) => {
        products.push({
            name: coverable?.flavourName ?? '',
            id: coverable?.fixedId?.length ? coverable.fixedId : 'None',
            group: productGroup.insurance,
            price: [
                Math.trunc(coverable.monthlyPrice?.amount ?? 0),
                Math.trunc(coverable.yearlyPrice?.amount ?? 0)
            ],
            coverages: coverable.coverages?.map((coverage: CoverageDTO | OnsaCoverageDTO) => {
                return convertToProductCoverage(coverage);
            })
        });
    });

    return products;
}

function trackWizardNavigation(pageName: WizardPageComponentName): void {
    if (pageName === 'ReceiptPage') {
        void c9Track.navigate(
            `${window.location.origin}${window.location.pathname}kvittering/${window.location.search}`
        );
        return;
    }
    void c9Track.navigate(`${window.location.origin}${window.location.pathname}${pageName}`);
}

export {
    track,
    trackAddProduct,
    trackRemoveProduct,
    trackPurchase,
    trackCheckout,
    trackViewProduct,
    trackWizardNavigation
};
